import { isRecruiting, verticalFrom, type Vertical } from "@/lib/verticalCopy";

/**
 * Which look the two magic-link journeys (`/verify/:token`, `/voucher-flow`)
 * render: the partner look (the white-first layout Article and Feather
 * introduced, `articlePresentation` in the pages) or the default one.
 *
 * ## The rule (Juan, 2026-10-16)
 *
 * The partner look is the default for every recruiting org. A live journey
 * renders it when
 *
 * - the hostname tenant's `branding.partner_key` is one that brings its own
 *   partner palette (`article`, `feather`), unchanged from before, or
 * - the session's org is `hr_recruiting` (`org_industry` off
 *   `/sessions/:token/status`).
 *
 * Leasing and an unknown industry keep the default look. Never key this off an
 * org name, a workflow key or a subdomain: the vertical is
 * `organizations.industry`.
 *
 * The pages and `AppShell` both read these two functions, so the page body and
 * the chrome around it cannot disagree.
 */

/**
 * The two routes whose page publishes its session vertical: `/verify/:token`
 * and `/voucher-flow`, exactly as the route table declares them. Anything else
 * under those prefixes is the not-found page, which publishes nothing and so
 * must not wait for a session that will never answer.
 */
export function isSessionJourneyPath(pathname: string): boolean {
  return /^\/verify\/[^/]+\/?$/.test(pathname) || pathname === "/voucher-flow" || pathname === "/voucher-flow/";
}

/** Partner keys whose tenant brings its own palette to the partner look. */
const PARTNER_LOOK_KEYS: readonly string[] = ["article", "feather"];

export function isPartnerLookKey(partnerKey: string | null | undefined): boolean {
  return PARTNER_LOOK_KEYS.includes(partnerKey ?? "");
}

/**
 * The page's answer. `industry` is the raw wire value of a LIVE session, or
 * undefined while it loads and on a demo route (which keeps its own look).
 */
export function usesPartnerLook(
  partnerKey: string | null | undefined,
  industry: string | null | undefined,
): boolean {
  return isPartnerLookKey(partnerKey) || isRecruiting(verticalFrom(industry));
}

/**
 * The shell's answer, which also has to say "not known yet": the shell paints
 * before the page has its session, and painting the default look first would
 * flash it at every recruiting applicant before the partner look settles.
 *
 * - `partner`: the partner layout.
 * - `default`: the default look, exactly as before.
 * - `pending`: a neutral shell (no header or footer chrome) until the session
 *   and the hostname tenant have both answered.
 *
 * `sessionVertical` is what the page published (`lib/sessionVertical.ts`):
 * undefined until the page has settled, null when it settled without a live
 * session vertical (an error, an expired link, a demo route).
 */
export type FlowLook = "partner" | "default" | "pending";

export function flowLookFor({
  partnerKey,
  isSessionFlow,
  tenantLoading,
  sessionVertical,
}: {
  partnerKey: string | null | undefined;
  /** `isSessionJourneyPath`: the routes the session decides. */
  isSessionFlow: boolean;
  tenantLoading: boolean;
  sessionVertical: Vertical | null | undefined;
}): FlowLook {
  if (isPartnerLookKey(partnerKey)) return "partner";
  if (!isSessionFlow) return "default";
  if (tenantLoading || sessionVertical === undefined) return "pending";
  return sessionVertical !== null && isRecruiting(sessionVertical) ? "partner" : "default";
}
